'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { listingService } from '@/services/listingService';
import FilterPanel from './FilterPanel';
import SortControls from './SortControls';
import EquipmentCard from './EquipmentCard';
import AdZone from './AdZone';
import Pagination from './Pagination';
import LoadingSkeleton from './LoadingSkeleton';
import RegistrationModal from '@/components/common/RegistrationModal';
import Icon from '@/components/ui/AppIcon';

interface FilterOptions {
  category: string;
  priceRange: [number, number];
  location: string;
  condition: string;
  manufacturer: string;
  yearRange: [number, number];
  keyword: string;
}

interface Equipment {
  id: string;
  title: string;
  slug: string;
  price: number;
  location: string;
  condition: string;
  year: number;
  manufacturer: string;
  category: string;
  images: Array<{url: string;alt: string;}>;
  seller: {
    name: string;
    rating: number;
    verified: boolean;
    phone: string;
  };
  specifications: {
    hours?: number;
    weight?: string;
    power?: string;
  };
  featured: boolean;
  listedDate: string;
}

const MarketplaceInteractive = () => {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [equipmentData, setEquipmentData] = useState<Equipment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isDesktopFilterOpen, setIsDesktopFilterOpen] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('relevance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [registrationAction, setRegistrationAction] = useState<'whatsapp' | 'enquiry' | null>(null);
  const [pendingActionData, setPendingActionData] = useState<{equipmentId?: number;phone?: string;title?: string;} | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({
    category: 'All Categories',
    priceRange: [0, 1000000],
    location: 'All Locations',
    condition: 'All Conditions',
    manufacturer: 'All Manufacturers',
    yearRange: [1990, 2024],
    keyword: ''
  });

  // Define loadEquipmentData BEFORE useEffect to avoid TDZ error
  const loadEquipmentData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use getListings with status filter for approved listings only
      const result = await listingService.getListings({
        status: 'approved',
        search: filters.keyword || undefined,
        category_id: filters.category !== 'All Categories' ? filters.category : undefined
      });

      // Transform Supabase data to match Equipment interface
      const transformedData: Equipment[] = (result.listings || []).map((listing: any) => ({
        id: listing.id,
        title: listing.title,
        slug: listing.slug,
        price: listing.price || 0,
        location: listing.location || 'Location not specified',
        condition: listing.condition,
        year: listing.year || 0,
        manufacturer: listing.manufacturer || 'Unknown',
        category: listing.category?.name || 'Uncategorized',
        images: (listing.images || [])
          .sort((a: any, b: any) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
          .map((img: any) => ({
            url: img.image_url,
            alt: img.alt_text || listing.title
          })),
        seller: {
          name: listing.seller?.full_name || 'Seller',
          rating: 4.5, // Default rating - would come from seller profile
          verified: true, // Default - would come from seller profile
          phone: listing.seller?.phone || ''
        },
        specifications: {
          hours: listing.mileage_hours ? parseInt(listing.mileage_hours) : undefined,
          weight: listing.specifications?.weight || '',
          power: listing.specifications?.power || ''
        },
        featured: listing.is_featured || false,
        listedDate: listing.created_at
      }));

      setEquipmentData(transformedData);
      setIsLoading(false);
    } catch (err: any) {
      console.error('Marketplace page error:', err);
      setError(err?.message || 'An unexpected error occurred');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsHydrated(true);
    loadEquipmentData();
  }, []);

  // Add early return for server-side rendering
  if (!isHydrated) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-24 bg-gray-200 rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-96 bg-gray-200 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const handleFiltersChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  };

  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode);
  };

  const handleWhatsAppClick = (phone: string, title: string) => {
    if (!isHydrated) return;

    if (!isAuthenticated) {
      setRegistrationAction('whatsapp');
      setPendingActionData({ phone, title });
      setIsRegistrationModalOpen(true);
      return;
    }

    // Proceed with WhatsApp if authenticated
    const message = encodeURIComponent(`Hi, I'm interested in the ${title}. Can you provide more details?`);
    window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const handleEnquiryClick = (equipmentId: number) => {
    if (!isHydrated) return;

    if (!isAuthenticated) {
      setRegistrationAction('enquiry');
      setPendingActionData({ equipmentId });
      setIsRegistrationModalOpen(true);
      return;
    }

    // Proceed with enquiry if authenticated
    alert(`Enquiry form would open for equipment ID: ${equipmentId}`);
  };

  const handleRegistrationSuccess = () => {
    // Execute the pending action after successful registration
    if (pendingActionData && registrationAction) {
      if (registrationAction === 'whatsapp' && pendingActionData.phone && pendingActionData.title) {
        const message = encodeURIComponent(`Hi, I'm interested in the ${pendingActionData.title}. Can you provide more details?`);
        window.open(`https://wa.me/${pendingActionData.phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
      } else if (registrationAction === 'enquiry' && pendingActionData.equipmentId) {
        alert(`Enquiry form would open for equipment ID: ${pendingActionData.equipmentId}`);
      }
    }

    // Clear pending action
    setPendingActionData(null);
    setRegistrationAction(null);
  };

  const filteredAndSortedEquipment = equipmentData
    .filter((equipment) => {
      if (filters.category !== 'All Categories' && equipment.category !== filters.category) return false;
      if (filters.location !== 'All Locations' && equipment.location !== filters.location) return false;
      if (filters.condition !== 'All Conditions' && equipment.condition !== filters.condition) return false;
      if (filters.manufacturer !== 'All Manufacturers' && equipment.manufacturer !== filters.manufacturer) return false;
      if (equipment.price < filters.priceRange[0] || equipment.price > filters.priceRange[1]) return false;
      if (equipment.year < filters.yearRange[0] || equipment.year > filters.yearRange[1]) return false;
      if (filters.keyword && !equipment.title.toLowerCase().includes(filters.keyword.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'price':
          comparison = a.price - b.price;
          break;
        case 'date':
          comparison = new Date(a.listedDate).getTime() - new Date(b.listedDate).getTime();
          break;
        case 'year':
          comparison = a.year - b.year;
          break;
        case 'alphabetical':
          comparison = a.title.localeCompare(b.title);
          break;
        default:
          comparison = (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

  const itemsPerPage = 12;
  const totalPages = Math.ceil(filteredAndSortedEquipment.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEquipment = filteredAndSortedEquipment.slice(startIndex, startIndex + itemsPerPage);

  if (!isHydrated || isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="ExclamationTriangleIcon" size={48} className="mx-auto text-destructive mb-4" />
        <h3 className="font-heading text-xl font-bold text-foreground mb-2">Error Loading Equipment</h3>
        <p className="text-text-secondary mb-4">{error}</p>
        <button
          onClick={loadEquipmentData}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-industrial font-heading font-bold uppercase tracking-wide hover:bg-accent transition-industrial"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Banner Advertisement */}
      <AdZone zone="top_banner" className="h-24 w-full rounded-industrial" />

      {/* Authentication Status Banner */}
      {!isAuthenticated && (
        <div className="bg-primary/10 border border-primary/20 rounded-industrial p-4">
          <div className="flex items-center space-x-3">
            <Icon name="InformationCircleIcon" size={20} className="text-primary" />
            <div className="flex-1">
              <p className="text-foreground font-medium">
                <strong>Register to unlock seller contact information</strong>
              </p>
              <p className="text-text-secondary text-sm">
                Join thousands of machinery professionals to access WhatsApp contacts and send enquiries.
              </p>
            </div>
            <button
              onClick={() => setIsRegistrationModalOpen(true)}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-sm hover:bg-accent transition-industrial"
            >
              Get Started Free
            </button>
          </div>
        </div>
      )}

      {/* Filter Toggle and Mobile Filter Button */}
      <div className="flex items-center justify-between">
        {/* Desktop Filter Toggle */}
        <div className="hidden lg:flex items-center space-x-4">
          <button
            onClick={() => setIsDesktopFilterOpen(!isDesktopFilterOpen)}
            className="flex items-center space-x-2 px-4 py-2 bg-surface border border-border rounded-industrial hover:bg-muted transition-industrial"
          >
            <Icon name="FunnelIcon" size={16} />
            <span className="font-heading font-bold uppercase tracking-wide text-sm">
              {isDesktopFilterOpen ? 'Hide Filters' : 'Show Filters'}
            </span>
            {!isDesktopFilterOpen && (
              <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-bold">
                {Object.values(filters).filter((f) =>
                  f !== 'All Categories' && f !== 'All Locations' &&
                  f !== 'All Conditions' && f !== 'All Manufacturers' &&
                  f !== ''
                ).length}
              </span>
            )}
          </button>
        </div>

        {/* Mobile Filter Button */}
        <div className="lg:hidden">
          <button
            onClick={() => setIsMobileFilterOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-industrial font-heading font-bold uppercase tracking-wide"
          >
            <Icon name="FunnelIcon" size={16} />
            <span>Filters</span>
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Collapsible Desktop Filter Panel */}
        {isDesktopFilterOpen && (
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-24">
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                resultCount={filteredAndSortedEquipment.length}
              />
            </div>
          </div>
        )}

        {/* Mobile Filter Panel */}
        <FilterPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          resultCount={filteredAndSortedEquipment.length}
          isMobile={true}
          isOpen={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
        />

        {/* Main Content - Now takes full width when filters are hidden */}
        <div className="flex-1 space-y-6">
          {/* Sort Controls */}
          <SortControls
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            resultCount={filteredAndSortedEquipment.length}
          />

          {/* Equipment Grid - Responsive columns based on available space */}
          {paginatedEquipment.length > 0 ? (
            <div className={`grid gap-6 ${
              viewMode === 'grid'
                ? isDesktopFilterOpen
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3' :'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5' :'grid-cols-1'
            }`}>
              {paginatedEquipment.map((equipment, index) => (
                <div key={equipment.id}>
                  <EquipmentCard
                    equipment={equipment}
                    viewMode={viewMode}
                    onWhatsAppClick={handleWhatsAppClick}
                    onEnquiryClick={handleEnquiryClick}
                    isAuthenticated={isAuthenticated}
                  />
                  
                  {/* Inline Ad every 8 items in grid mode */}
                  {viewMode === 'grid' && (index + 1) % 8 === 0 && (
                    <div className={`${
                      isDesktopFilterOpen
                        ? 'md:col-span-2 lg:col-span-2 xl:col-span-3' :'md:col-span-2 lg:col-span-3 xl:col-span-4 2xl:col-span-5'
                    }`}>
                      <AdZone zone="listing_grid_inline" className="h-32 rounded-industrial" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="MagnifyingGlassIcon" size={48} className="mx-auto text-text-secondary mb-4" />
              <h3 className="font-heading text-xl font-bold text-foreground mb-2">No Equipment Found</h3>
              <p className="text-text-secondary mb-4">
                Try adjusting your filters or search terms to find more equipment.
              </p>
              <button
                onClick={() => handleFiltersChange({
                  category: 'All Categories',
                  priceRange: [0, 1000000],
                  location: 'All Locations',
                  condition: 'All Conditions',
                  manufacturer: 'All Manufacturers',
                  yearRange: [1990, 2024],
                  keyword: ''
                })}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-industrial font-heading font-bold uppercase tracking-wide hover:bg-accent transition-industrial"
              >
                Clear All Filters
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="mt-8"
            />
          )}

          {/* Bottom Advertisement */}
          <AdZone zone="bottom_banner" className="h-32 w-full rounded-industrial mt-8" />
        </div>
      </div>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        title="Join Machinery Movers"
        description={
          registrationAction === 'whatsapp' 
            ? "Get instant access to seller contact information and start connecting with equipment dealers." 
            : "Register to send enquiries and connect with verified equipment sellers across the country."
        }
        actionText="Get Contact Details"
        onSuccess={handleRegistrationSuccess} />

    </div>);

};

export default MarketplaceInteractive;